import { DateTime } from 'luxon';
import React, { HTMLProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Portfolio } from '../../../../types/Portfolio';
import { mergedItems } from '../../../../utils/functions';
import { SectionDisclosure } from '../../../general/Disclosure';
import {
    mapTopText,
    mapTopChart,
    mapSectionPages,
    SectionsContainer,
    Section,
} from '../comparison-bars/FundSectionFunctions';
import {
    ComparisonStickyCardDetailsBasicCharacteristics,
    ComparisonStickyCardDetailsHistoricalReturns,
    ComparisonStickyCardDetailsHistoricalReturnsSmall,
    ComparisonStickyCardDetailsStrategy,
    ComparisonStickyCardDetailsUnderlyingBonds,
    ComparisonStickyCardDetailsUnderlyingStocks,
} from '../comparison-sticky-card/ComparisonStickyCardDetails';
import { ComparisonStickyCardDetailsRiskCharacteristics } from '../comparison-sticky-card/ComparisonStickyCardDetails';

export type TintedPortfolio = Portfolio & {
    tint: string;
};

export const PortfolioCompare: React.FC<
    {
        portfolios: TintedPortfolio[];
        startDate: string;
        endDate?: string;
    } & HTMLProps<HTMLDivElement>
> = ({ className, portfolios, startDate, endDate = 'Nu' }) => {
    const { t } = useTranslation('funds');

    const PortfolioTopText = portfolios.map((portfolio, index) => mapTopText(portfolio, index, startDate, endDate));
    const PortfolioTopChart = portfolios.map((portfolio, index) => mapTopChart(portfolio, index));

    const PortfolioStrategy = portfolios.map((portfolio, index) => {
        return (
            <div
                key={portfolio.id + 'strategy'}
                className={`flex-1 px-4 py-1 ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}
            >
                <ComparisonStickyCardDetailsStrategy asset={portfolio} />
            </div>
        );
    });

    const PortfolioCoreProperties = portfolios.map((portfolio, index) => {
        return (
            <div
                key={portfolio.id + 'core'}
                className={`flex-1 px-4 py-1 ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}
            >
                <ComparisonStickyCardDetailsBasicCharacteristics asset={portfolio} />
            </div>
        );
    });

    const PortfolioUnderlyingBonds = portfolios.map((portfolio, index) => {
        return (
            <div
                key={portfolio.id + 'bonds'}
                className={`flex-1 px-4 py-1 ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}
            >
                <ComparisonStickyCardDetailsUnderlyingBonds asset={portfolio} />
            </div>
        );
    });
    const PortfolioUnderlyingStocks = portfolios.map((portfolio, index) => {
        return (
            <div
                key={portfolio.id + 'stocks'}
                className={`flex-1 px-4 py-1 ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}
            >
                <ComparisonStickyCardDetailsUnderlyingStocks asset={portfolio} />
            </div>
        );
    });

    const PortfolioRiskCharactaristic = portfolios.map((portfolio, index) => {
        return (
            <div
                key={portfolio.id + 'risk'}
                className={`flex-1 px-4 py-1 ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}
            >
                <ComparisonStickyCardDetailsRiskCharacteristics asset={portfolio} />
            </div>
        );
    });

    const PortfolioHistoricalReturns = portfolios.map((portfolio, index) => {
        return (
            <div
                key={portfolio.id + 'returns'}
                className={`flex-1 px-4 py-1 ${index > 0 ? 'lg:border-l-2 border-gray-300' : ''}`}
            >
                <ComparisonStickyCardDetailsHistoricalReturns className="hidden sm:grid" asset={portfolio} />
                <ComparisonStickyCardDetailsHistoricalReturnsSmall className="sm:hidden" asset={portfolio} />
            </div>
        );
    });

    const PortfolioSectors = portfolios.map((portfolio, index) =>
        mapSectionPages({
            index,
            sections: [
                {
                    title: `${t('funds-comparison-screen-sectors-stock')}`,
                    items: mergedItems(
                        portfolios.map((p) => p.sectors.stocks || []),
                        portfolio.sectors.stocks || [],
                    ),
                    referenceDate: DateTime.fromISO(portfolio.referenceDateSectors.stocks),
                },
                {
                    title: `${t('funds-comparison-screen-sectors-bond')}`,
                    items: mergedItems(
                        portfolios.map((p) => p.sectors.bonds || []),
                        portfolio.sectors.bonds || [],
                    ),
                    referenceDate: portfolio.referenceDateSectors.bonds
                        ? DateTime.fromISO(portfolio.referenceDateSectors.bonds)
                        : undefined,
                },
            ],
            appendix: 'remaining',
        }),
    );
    const PortfolioRegionDistribution = portfolios.map((portfolio, index) =>
        mapSectionPages({
            index,
            sections: [
                {
                    title: `${t('funds-comparison-screen-markets-ring')} ${t('general-stocks')}`,
                    items: mergedItems(
                        [...portfolios.map((p) => p.regions.stocks || [])],
                        portfolio.regions.stocks || [],
                    ),
                    referenceDate: DateTime.fromISO(portfolio.referenceDateRegions.stocks),
                },
                {
                    title: `${t('funds-comparison-screen-markets-ring')} ${t('general-bonds')}`,
                    items: mergedItems(
                        [...portfolios.map((p) => p.regions.bonds || [])],
                        portfolio.regions.bonds || [],
                    ),
                    referenceDate: portfolio.referenceDateSectors.bonds
                        ? DateTime.fromISO(portfolio.referenceDateSectors.bonds)
                        : undefined,
                },
            ],
            appendix: 'remaining',
        }),
    );
    const PortfolioCreditQuality = portfolios.map((portfolio, index) =>
        mapSectionPages({
            index,
            sections: [
                {
                    title: `${t('funds-comparison-screen-quality')} ${t('general-bonds')}`,
                    items: mergedItems(
                        portfolios.map((p) => p.quality || []),
                        portfolio.quality || [],
                    ),
                    referenceDate: portfolio.referenceDateQuality
                        ? DateTime.fromISO(portfolio.referenceDateQuality)
                        : undefined,
                },
            ],
            appendix: 'none',
        }),
    );
    const PortfolioMaturity = portfolios.map((portfolio, index) =>
        mapSectionPages({
            index,
            sections: [
                {
                    title: `${t('funds-comparison-screen-duration')} ${t('general-bonds')}`,
                    items: mergedItems(
                        portfolios.map((p) => p.maturity || []),
                        portfolio.maturity || [],
                    ),
                    referenceDate: portfolio.referenceDateMaturity
                        ? DateTime.fromISO(portfolio.referenceDateMaturity)
                        : undefined,
                },
            ],
            appendix: 'none',
        }),
    );
    const sections = [
        { title: t('funds-comparison-screen-investment-strategy'), nodes: PortfolioStrategy, mobileOnly: true },
        { title: t('funds-comparison-screen-core-properties'), nodes: PortfolioCoreProperties, mobileOnly: true },
        { title: t('funds-comparison-screen-underlying-bonds'), nodes: PortfolioUnderlyingBonds, mobileOnly: true },
        { title: t('funds-comparison-screen-underlying-stocks'), nodes: PortfolioUnderlyingStocks, mobileOnly: true },
        {
            title: t('funds-comparison-screen-risk-characteristics'),
            nodes: PortfolioRiskCharactaristic,
            mobileOnly: true,
        },
        { title: t('funds-comparison-screen-historical-returns'), nodes: PortfolioHistoricalReturns, mobileOnly: true },
        { title: t('funds-comparison-screen-sectors'), nodes: PortfolioSectors },
        { title: t('funds-comparison-screen-markets'), nodes: PortfolioRegionDistribution },
        { title: t('funds-comparison-screen-quality'), nodes: PortfolioCreditQuality },
        { title: t('funds-comparison-screen-duration'), nodes: PortfolioMaturity },
    ];

    return (
        <SectionsContainer className={className}>
            <Section pages={PortfolioTopText} />
            <Section pages={PortfolioTopChart} />
            {sections.map((section, index) => {
                return (
                    <SectionDisclosure
                        title={section.title}
                        key={index}
                        className={`${section.mobileOnly === true ? 'xl:hidden' : ''}`}
                    >
                        <div className="flex flex-1 flex-col">
                            <Section pages={section.nodes} />
                        </div>
                    </SectionDisclosure>
                );
            })}
        </SectionsContainer>
    );
};
